import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, Download, Eye, Edit2, Save, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminCareersTab = ({ authHeaders, showMsg, loading, setLoading, token }) => {
  const [view, setView] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [jobForm, setJobForm] = useState({ title: '', department: '', location: '', employment_type: 'Full-time', description: '', responsibilities: '', qualifications: '', experience: '', deadline: '', status: 'draft' });
  const [editingJob, setEditingJob] = useState(null);
  const [expandedApp, setExpandedApp] = useState({});
  const [filterJob, setFilterJob] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const fetchAll = useCallback(async () => {
    try {
      const [jobRes, appRes] = await Promise.all([
        fetch(`${API}/api/careers/admin/jobs`, { headers: authHeaders() }),
        fetch(`${API}/api/careers/admin/applications`, { headers: authHeaders() })
      ]);
      if (jobRes.ok) setJobs(await jobRes.json());
      if (appRes.ok) setApplications(await appRes.json());
    } catch {}
  }, [authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveJob = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingJob) {
        await fetch(`${API}/api/careers/admin/jobs/${editingJob}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(jobForm) });
        showMsg('Job updated!');
      } else {
        await fetch(`${API}/api/careers/admin/jobs`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(jobForm) });
        showMsg('Job created!');
      }
      setJobForm({ title: '', department: '', location: '', employment_type: 'Full-time', description: '', responsibilities: '', qualifications: '', experience: '', deadline: '', status: 'draft' });
      setEditingJob(null);
      fetchAll();
    } catch { showMsg('Failed', 'error'); }
    setLoading(false);
  };

  const editJob = (job) => {
    setJobForm({ title: job.title, department: job.department, location: job.location, employment_type: job.employment_type, description: job.description || '', responsibilities: job.responsibilities || '', qualifications: job.qualifications || '', experience: job.experience || '', deadline: job.deadline || '', status: job.status });
    setEditingJob(job.id);
  };

  const toggleJobStatus = async (job) => {
    setLoading(true);
    const newStatus = job.status === 'published' ? 'draft' : 'published';
    await fetch(`${API}/api/careers/admin/jobs/${job.id}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ status: newStatus }) });
    fetchAll(); showMsg(`Job ${newStatus === 'published' ? 'published' : 'unpublished'}`);
    setLoading(false);
  };

  const deleteJob = async (id) => {
    if (!window.confirm('Deactivate this job?')) return;
    setLoading(true);
    await fetch(`${API}/api/careers/admin/jobs/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchAll(); showMsg('Job deactivated');
    setLoading(false);
  };

  const updateAppStatus = async (id, status, notes) => {
    setLoading(true);
    await fetch(`${API}/api/careers/admin/applications/${id}/status`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ status, notes }) });
    fetchAll(); showMsg(`Status: ${status}`);
    setLoading(false);
  };

  const statusColors = { New: 'bg-blue-50 text-blue-700', 'Under Review': 'bg-yellow-50 text-yellow-700', Shortlisted: 'bg-purple-50 text-purple-700', 'Interview Scheduled': 'bg-indigo-50 text-indigo-700', Selected: 'bg-green-50 text-green-700', Rejected: 'bg-red-50 text-red-600' };
  const appStatuses = ['New', 'Under Review', 'Shortlisted', 'Interview Scheduled', 'Selected', 'Rejected'];

  const filteredApps = applications.filter(a => {
    if (filterJob !== 'all' && a.job_id !== filterJob) return false;
    if (filterStatus !== 'all' && a.status !== filterStatus) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex gap-2 mb-4">
        <button onClick={() => setView('jobs')} className={`px-4 py-2 text-sm font-semibold rounded-lg cursor-pointer ${view === 'jobs' ? 'bg-[#5C7A3A] text-white' : 'bg-white text-[#7a6555] border border-[#E8DFD3]'}`} data-testid="careers-jobs-tab">Jobs ({jobs.length})</button>
        <button onClick={() => setView('applications')} className={`px-4 py-2 text-sm font-semibold rounded-lg cursor-pointer ${view === 'applications' ? 'bg-[#5C7A3A] text-white' : 'bg-white text-[#7a6555] border border-[#E8DFD3]'}`} data-testid="careers-apps-tab">Applications ({applications.length})</button>
      </div>

      {/* Jobs View */}
      {view === 'jobs' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-1 border border-[#E8DFD3] h-fit bg-white shadow-sm">
            <CardContent className="p-5">
              <h3 className="font-bold text-[#2C1810] text-sm flex items-center gap-2 mb-4">
                {editingJob ? <><Edit2 size={14} className="text-[#C4844A]" /> Edit Job</> : <><Plus size={14} className="text-[#5C7A3A]" /> Add Job Opening</>}
              </h3>
              <form onSubmit={saveJob} className="space-y-2">
                <Input value={jobForm.title} onChange={e => setJobForm(f => ({ ...f, title: e.target.value }))} placeholder="Job Title" required className="text-xs" data-testid="career-job-title" />
                <div className="grid grid-cols-2 gap-2">
                  <Input value={jobForm.department} onChange={e => setJobForm(f => ({ ...f, department: e.target.value }))} placeholder="Department" required className="text-xs" data-testid="career-job-dept" />
                  <Input value={jobForm.location} onChange={e => setJobForm(f => ({ ...f, location: e.target.value }))} placeholder="Location" required className="text-xs" data-testid="career-job-location" />
                </div>
                <select value={jobForm.employment_type} onChange={e => setJobForm(f => ({ ...f, employment_type: e.target.value }))} className="w-full h-9 border border-[#E8DFD3] rounded-md text-xs px-3 text-[#2C1810]" data-testid="career-job-type">
                  <option>Full-time</option><option>Part-time</option><option>Contract</option><option>Internship</option>
                </select>
                <Textarea value={jobForm.description} onChange={e => setJobForm(f => ({ ...f, description: e.target.value }))} placeholder="Job Description" required rows={3} className="text-xs" data-testid="career-job-desc" />
                <Textarea value={jobForm.responsibilities} onChange={e => setJobForm(f => ({ ...f, responsibilities: e.target.value }))} placeholder="Responsibilities (one per line)" rows={3} className="text-xs" />
                <Textarea value={jobForm.qualifications} onChange={e => setJobForm(f => ({ ...f, qualifications: e.target.value }))} placeholder="Qualifications (one per line)" rows={2} className="text-xs" />
                <Input value={jobForm.experience} onChange={e => setJobForm(f => ({ ...f, experience: e.target.value }))} placeholder="Experience (e.g., 2-4 years)" className="text-xs" />
                <Input type="date" value={jobForm.deadline} onChange={e => setJobForm(f => ({ ...f, deadline: e.target.value }))} className="text-xs" />
                <select value={jobForm.status} onChange={e => setJobForm(f => ({ ...f, status: e.target.value }))} className="w-full h-9 border border-[#E8DFD3] rounded-md text-xs px-3 text-[#2C1810]" data-testid="career-job-status">
                  <option value="draft">Draft</option><option value="published">Published</option>
                </select>
                <div className="flex gap-2">
                  <Button type="submit" disabled={loading} className="flex-1 bg-[#5C7A3A] hover:bg-[#4a6530] text-white cursor-pointer text-xs" data-testid="save-job-btn">
                    <Save size={12} className="mr-1" /> {editingJob ? 'Update' : 'Create'} Job
                  </Button>
                  {editingJob && (
                    <Button type="button" onClick={() => { setEditingJob(null); setJobForm({ title: '', department: '', location: '', employment_type: 'Full-time', description: '', responsibilities: '', qualifications: '', experience: '', deadline: '', status: 'draft' }); }} variant="outline" className="border-[#E8DFD3] cursor-pointer text-xs"><X size={12} /></Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          <div className="lg:col-span-2 space-y-2">
            {jobs.filter(j => j.active !== false).map(job => (
              <Card key={job.id} className="border border-[#E8DFD3] bg-white shadow-sm" data-testid={`admin-job-${job.id}`}>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#2C1810] truncate">{job.title}</p>
                    <p className="text-xs text-[#7a6555]">{job.department} · {job.location} · {job.employment_type}</p>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${job.status === 'published' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{job.status}</span>
                  <Button onClick={() => toggleJobStatus(job)} variant="ghost" size="sm" className="text-[#5C7A3A] hover:bg-[#5C7A3A]/5 cursor-pointer text-xs">{job.status === 'published' ? 'Unpublish' : 'Publish'}</Button>
                  <Button onClick={() => editJob(job)} variant="ghost" size="sm" className="text-[#C4844A] hover:bg-[#C4844A]/5 cursor-pointer"><Edit2 size={14} /></Button>
                  <Button onClick={() => deleteJob(job.id)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-50 cursor-pointer"><Trash2 size={14} /></Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Applications View */}
      {view === 'applications' && (
        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            <select value={filterJob} onChange={e => setFilterJob(e.target.value)} className="h-8 border border-[#E8DFD3] rounded-md text-xs px-2 text-[#2C1810]" data-testid="filter-job">
              <option value="all">All Positions</option>
              {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="h-8 border border-[#E8DFD3] rounded-md text-xs px-2 text-[#2C1810]" data-testid="filter-status">
              <option value="all">All Statuses</option>
              {appStatuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <span className="text-xs text-[#7a6555] self-center ml-2">{filteredApps.length} applications</span>
          </div>

          {filteredApps.length === 0 ? (
            <div className="text-center py-12 text-[#7a6555]">No applications yet.</div>
          ) : filteredApps.map(app => (
            <Card key={app.id} className="border border-[#E8DFD3] bg-white shadow-sm mb-3" data-testid={`application-${app.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                  <div>
                    <p className="font-bold text-[#2C1810] text-sm">{app.name}</p>
                    <p className="text-xs text-[#7a6555]">{app.email} · {app.mobile}</p>
                    <p className="text-xs text-[#C4844A] font-medium">{app.job_title}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={app.status} onChange={e => updateAppStatus(app.id, e.target.value, app.admin_notes)} className={`text-xs font-semibold px-2 py-1 rounded-full border-0 cursor-pointer ${statusColors[app.status] || 'bg-gray-50'}`} data-testid={`app-status-${app.id}`}>
                      {appStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <a href={`${API}/api/careers/admin/resume/${app.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#5C7A3A] font-medium hover:underline" data-testid={`download-resume-${app.id}`} onClick={(e) => {
                      e.preventDefault();
                      fetch(`${API}/api/careers/admin/resume/${app.id}`, { headers: { 'Authorization': `Bearer ${token}` } })
                        .then(r => r.blob())
                        .then(blob => {
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = app.resume_filename || 'resume.pdf';
                          a.click();
                          window.URL.revokeObjectURL(url);
                        });
                    }}><Download size={12} /> Resume</a>
                  </div>
                </div>
                <button onClick={() => setExpandedApp(prev => ({ ...prev, [app.id]: !prev[app.id] }))} className="text-xs text-[#5C7A3A] cursor-pointer hover:underline">{expandedApp[app.id] ? 'Hide' : 'View'} details</button>
                {expandedApp[app.id] && (
                  <div className="mt-2 pt-2 border-t border-[#E8DFD3] space-y-1 text-xs text-[#7a6555]">
                    {app.location && <p>Location: {app.location}</p>}
                    {app.experience && <p>Experience: {app.experience}</p>}
                    {app.message && <p>Message: {app.message}</p>}
                    <p>Applied: {new Date(app.created_at).toLocaleDateString()}</p>
                    <div className="mt-2">
                      <label className="text-xs font-medium text-[#2C1810] mb-1 block">Admin Notes</label>
                      <Textarea defaultValue={app.admin_notes || ''} onBlur={e => { if (e.target.value !== (app.admin_notes || '')) updateAppStatus(app.id, app.status, e.target.value); }} placeholder="Internal notes..." rows={2} className="text-xs" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCareersTab;
